import React, { useEffect, useState, useCallback, useRef } from 'react'; // useCallback 추가
import { Card, Button, Form, Row, Col, InputGroup, Modal } from 'react-bootstrap';
import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.min.css';
import { Korean } from 'flatpickr/dist/l10n/ko.js';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter_paging';
import AdvanceTablePagination from 'components/common/advance-table/AdvanceTablePagination_paging';
import { fetchManageLog_Admin, getCompanyList } from 'hooks/CallApi';
import {
  formatDate_log,
  formatToCustomISO
} from 'components/common/SunnyUtils';
import { useAppContext } from 'providers/AppProvider';
import styled from 'styled-components';
import CheckOutModal from 'components/modal/CheckOutModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faClipboardList } from '@fortawesome/free-solid-svg-icons';

// --- Styled Components ---
const PageContainer = styled.div`
  padding: 2rem 1rem;
  max-width: 1400px;
  margin: 0 auto;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e2e8f0;
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #e0e7ff 0%, #c3d4fe 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3b82f6;
  font-size: 1.5rem;
  box-shadow: 0 4px 6px rgba(59, 130, 246, 0.1);
`;

const PageTitle = styled.h2`
  margin: 0;
  font-weight: 700;
  color: #1e293b;
  font-size: 1.75rem;
  letter-spacing: -0.5px;
`;

const PageSubtitle = styled.p`
  margin: 4px 0 0 0;
  color: #64748b;
  font-size: 0.95rem;
`;

const StyledCard = styled(Card)`
  border: none;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.02);
  overflow: hidden;
  transition: box-shadow 0.3s ease;
  
  &:hover {
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08);
  }
`;
const columns = [
  {
    accessorKey: 'num',
    header: '순번',
    meta: { headerProps: { className: 'text-900' } },
    enableSorting: false
  },
  {
    accessorKey: 'jobdate',
    header: '작업일시',
    meta: { maxlength: 19, headerProps: { className: 'text-900' } }
  },
  {
    accessorKey: 'org',
    header: '소속',
    meta: { headerProps: { className: 'text-900' } },
    enableSorting: false
  },
  {
    accessorKey: 'Drole',
    header: '역할',
    meta: { maxlength: 7, headerProps: { className: 'text-900' } },
    enableSorting: false
  },
  {
    accessorKey: 'uid',
    header: '아이디',
    meta: { maxlength: 20, headerProps: { className: 'text-900' } },
    enableSorting: false
  },
  {
    accessorKey: 'job',
    header: '작업내역',
    meta: { maxlength: 20, headerProps: { className: 'text-900' } },
    enableSorting: false
  },
  {
    accessorKey: 'result',
    header: '결과',
    meta: { headerProps: { className: 'text-900' } },
    enableSorting: false
  }
];

// JSON 표시 컴포넌트
const JsonDisplay = ({ data }) => {
  if (!data) return <div>요청 상세 데이터가 없습니다.</div>;

  try {
    const jsonString = typeof data === 'string' ? data : JSON.stringify(data, null, 2);
    return (
      <pre style={{
        whiteSpace: 'pre-wrap',
        fontSize: '12px',
        backgroundColor: '#f8f9fa',
        padding: '15px',
        borderRadius: '4px',
        border: '1px solid #dee2e6',
        maxHeight: '400px',
        overflow: 'auto',
        margin: 0
      }}>
        {jsonString}
      </pre>
    );
  } catch (error) {
    return <div>JSON 파싱 오류: {error.message}</div>;
  }
};

function AdvanceTableExample() {
  const [tableData, setTableData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [requestDateRange, setRequestDateRange] = useState([]);
  const [policyId, setPolicyId] = useState('');
  const [org, setOrg] = useState('');
  const [role, setRole] = useState('');
  const [uid, setUid] = useState('');
  const [job, setJob] = useState('');
  const [options, setOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState('직접입력'); // 선택된 옵션 상태 추가

  const [result, setResult] = useState('');
  const [companies, setCompanies] = useState([]); // 회사 목록 상태

  const [modalShow, setModalShow] = useState(false);
  const [modalReShow, setModalReShow] = useState(false);
  const [roles, setRoles] = useState([]); // 역할 데이터 상태 추가.
  const [resultShow, setResultShow] = useState(false);

  // 모달 상태 관리
  const [detailModalShow, setDetailModalShow] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);

  const roleMapping = {
    '시스템 관리자': 'SA',
    '최고 관리자': 'ADMIN',
    '운영 관리자': 'MANAGER'
  };
  const [roleOptions, setRoleOptions] = useState(Object.keys(roleMapping)); // 셀렉트 박스에 표시할 역할 목록

  const [totalElements, setTotalElements] = useState(0);
  const [currentPage, setCurrentPage] = useState(0); // 현재 페이지
  const [perPage, setPerPage] = useState(10); // 페이지당 항목 수 (size)

  // 검색 버튼으로 확정된 필터 (입력 중에는 API 호출 방지)
  const [appliedFilters, setAppliedFilters] = useState({
    startDate: null,
    endDate: null,
    org: '',
    role: '',
    uid: '',
    job: '',
    result: ''
  });

  // 검색 버튼을 누르기 전에는 API 호출 금지
  const [searchApplied, setSearchApplied] = useState(false);

  const table = useAdvanceTable({
    data: filteredData,
    columns,
    sortable: true,
    selection: false,
    pagination: true,
    perPage,
    selectionColumnWidth: 30
  });
  const { setConfig } = useAppContext();

  // 텍스트 파일에서 옵션을 읽어오는 함수
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const response = await fetch('/options.txt');
        const text = await response.text();
        const optionsArray = text.split('\n').map(option => option.trim()).filter(option => option);
        setOptions(['직접입력', ...optionsArray]); // "직접입력" 옵션 추가
      } catch (error) {
        console.error('옵션을 불러오는 중 에러 발생:', error);
      }
    };

    fetchOptions();
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setJob(value);

    // 입력 값이 빈 문자열일 때 선택 상태 초기화
    if (value === '') {
      setSelectedOption('직접입력');
    }
  };

  const handleSelectChange = (e) => {
    const value = e.target.value;
    setSelectedOption(value); // 선택된 옵션 상태 업데이트
    if (value !== '직접입력') {
      setJob(value); // 입력 필드 값도 업데이트
    } else {
      setJob(''); // "직접입력" 선택 시 입력 필드 초기화
    }
  };

  // 필터 영역에서 Enter 키로 검색 실행
  const handleFilterKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  // API 호출 함수
  const loadData = useCallback(async (page, size) => {
    try {
      const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      // const response = await fetchManageLog(page, size); // API 호출
      const response = await fetchManageLog_Admin({
        page,
        size,
        // 날짜가 비설정(null)인 경우 오늘로 강제하지 않고 null 전달하여 전체 기간 조회를 유지
        searchStartTime: appliedFilters.startDate
          ? formatToCustomISO(appliedFilters.startDate)
          : null,
        searchEndTime: appliedFilters.endDate
          ? formatToCustomISO(appliedFilters.endDate)
          : null,
        timeZone: clientTimeZone,
        userId: appliedFilters.uid || null,
        userRole: appliedFilters.role ? roleMapping[appliedFilters.role] : null, // 한글 역할을 영어,
        taskHistory: appliedFilters.job || null,
        result: appliedFilters.result || null,
        insertTime: 'desc',
        companyId: appliedFilters.org || null // 운영 관리자( MANAGER )도 org 자동전달
      });
      const logData = response.data.content.map((item, index) => ({
        // num: index + 1 + page * size, // 순번 계산
        num: response.data.page.totalElements - (page * size + index), // 역순으로 순번 계산
        jobdate: item.insertTime
          ? formatDate_log(item.insertTime) // 포맷 함수 호출
          : '-',
        role: item.userRole,
        Drole:
          item.userRole === 'SA'
            ? '시스템 관리자'
            : item.userRole === 'ADMIN'
              ? '최고 관리자'
              : '운영 관리자',
        org: item.companyName,
        uid: item.userId,
        job: item.taskHistory,
        result:
          ['성공', '클라이언트 에러', '서버 에러', '기타'][item.result] ||
          '기타',
        requestDetails: item.requestDetails || null // requestDetails 추가
      }));
      setTotalElements(response.data.page.totalElements);
      setTableData(logData);
      setFilteredData(logData);

      // 역할 데이터 추출 및 중복 제거
      const uniqueRoles = [
        ...new Set(response.data.content.map(item => item.userRole))
      ];
      setRoles(uniqueRoles);
    } catch (error) {
      console.error('API 데이터 로드 에러:', error);
    }
  }, [appliedFilters]); // 검색 확정 값만 의존하도록 변경 (입력 중 API 호출 방지)

  // 페이지 변경 시 데이터 다시 로드
  useEffect(() => {
    setConfig('checkorg', '');
    setConfig('isNavbarVerticalCollapsed', true);
    if (!searchApplied) return; // 검색 확정 전에는 호출 금지
    loadData(currentPage, perPage);
  }, [searchApplied, currentPage, perPage, loadData]); // 검색 확정 이후에만 호출

  const handlePageChange = useCallback(async newPage => {
    setCurrentPage(newPage);
    table.resetRowSelection(); // 선택된 행 초기화

    // 페이지 변경 시 모달 닫기
    setDetailModalShow(false);
    setSelectedRowData(null);
  }, [table]);

  const handlePerPageChange = useCallback(newPerPage => {
    setPerPage(newPerPage); // 페이지 크기 업데이트
    setCurrentPage(0); // 첫 페이지로 이동 (효과 훅이 호출)

    // 페이지 크기 변경 시 모달 닫기
    setDetailModalShow(false);
    setSelectedRowData(null);
  }, []);

  const handleSearch = async () => {
    const [startDate, endDate] = requestDateRange;

    if (startDate && endDate && startDate > endDate) {
      setResultShow(true);
      return;
    }

    // 검색 시 모달 닫기
    setDetailModalShow(false);
    setSelectedRowData(null);

    // 입력된 값들을 확정하여 적용 (여기서는 API 호출하지 않음)
    setAppliedFilters({
      startDate: startDate || null,
      endDate: endDate || null,
      org,
      role,
      uid,
      job,
      result
    });
    setSearchApplied(true);
    setCurrentPage(0); // 첫 페이지부터 조회
  };

  useEffect(() => {
    // 회사 목록을 가져오는 API 호출
    const fetchCompanies = async () => {
      try {
        const response = await getCompanyList(); // getCompanyList 호출
        if (response) {
          const companyList = response.data.map(company => ({
            companyId: company.companyId,
            org: company.name
          }));
          setCompanies(companyList); // 회사 목록 상태 업데이트
        }
      } catch (error) {
        console.error('회사 목록을 가져오는 중 에러 발생:', error);
      }
    };

    fetchCompanies();
  }, []);

  const handleDefault = () => {
    setRequestDateRange([]);
    setOrg('');
    setRole('');
    setUid('');
    setJob('');
    setResult('');
    setSelectedOption('직접입력'); // 작업내역 콤보박스를 직접입력으로 초기화

    // 검색 조건도 비우고, 검색 확정 해제 (API 호출 안 함)
    setAppliedFilters({
      startDate: null,
      endDate: null,
      org: '',
      role: '',
      uid: '',
      job: '',
      result: ''
    });
    setSearchApplied(false);

    // 모달 닫기
    setDetailModalShow(false);
    setSelectedRowData(null);

    const datepicker_start =
      document.querySelector('#timepicker_start')._flatpickr;
    if (datepicker_start) {
      datepicker_start.clear(); // 기존 날짜 선택 제거
      datepicker_start.setDate([]); // 날짜 범위 초기화
    }
    const datepicker_end = document.querySelector('#timepicker_end')._flatpickr;
    if (datepicker_end) {
      datepicker_end.clear(); // 기존 날짜 선택 제거
      datepicker_end.setDate([]); // 날짜 범위 초기화
    }
  };

  // 모달 핸들러
  const handleRowClick = (row) => {
    setSelectedRowData(row);
    setDetailModalShow(true);
  };

  const handleCloseDetailModal = () => {
    setDetailModalShow(false);
    setSelectedRowData(null);
  };

  useEffect(() => {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0); // 오늘 00시 00분 00초

    const todayEnd = new Date();
    todayEnd.setHours(23, 59, 59, 999); // 오늘 23시 59분 59초

    // 초기 진입 시 UI에만 기본 날짜를 보여주고, 실제 검색 조건은 적용하지 않음
    setRequestDateRange([todayStart, todayEnd]);

    flatpickr('#timepicker_start', {
      mode: 'single',
      dateFormat: 'Y-m-d H:i',
      enableTime: true,
      time_24hr: true,
      disableMobile: true,
      position: 'below',
      locale: Korean,
      minDate: new Date().fp_incr(-180),
      defaultDate: todayStart,
      // onChange: (selectedDates) => setRequestDateRange(selectedDates)
      onChange: selectedDates => {
        setRequestDateRange(prev => [selectedDates[0], prev[1] || null]);
      }
    });

    flatpickr('#timepicker_end', {
      mode: 'single',
      dateFormat: 'Y-m-d H:i',
      enableTime: true,
      time_24hr: true,
      disableMobile: true,
      position: 'below',
      locale: Korean,
      minDate: new Date().fp_incr(-180),
      defaultDate: todayEnd,
      // onChange: (selectedDates) => setRequestDateRange(selectedDates)
      onChange: selectedDates => {
        setRequestDateRange(prev => [prev[0] || null, selectedDates[0]]);
      }
    });
  }, []);

  // 최초 진입 시 기본(오늘) 날짜로 자동 검색 트리거
  const didAutoSearchRef = useRef(false);
  useEffect(() => {
    if (didAutoSearchRef.current) return;
    if (requestDateRange && requestDateRange[0] && requestDateRange[1]) {
      didAutoSearchRef.current = true;
      handleSearch();
    }
  }, [requestDateRange, handleSearch]);

  const startInputRef = useRef(null);
  const endInputRef = useRef(null);

  return (
    <AdvanceTableProvider {...table}>
      <StyledCard className="mb-3">
        <Card.Body>
          <FilterContainer>
        {/* 검색 필터 */}
        <FilterInputs className="filter-inputs" onKeyDown={handleFilterKeyDown}>
          {/* 전체 레코드 수와 페이지네이션 셀렉트 */}
          <div className="d-flex align-items-center">
            <AdvanceTableFooter
              rowsPerPageSelection_filter
              onPageChange={(page, newPageSize) => {
                handlePerPageChange(newPageSize); // 페이지 크기 변경
                setCurrentPage(page); // 현재 페이지 설정
              }}
            />
          </div>

          <div className="mb-0 fs-10" style={{ position: 'relative' }}>
            <input
              id="timepicker_start"
              className="form-control datetimepicker"
              type="text"
              placeholder="2024-01-01 00:00"
              title="작업일시"
              ref={startInputRef} // input 요소 참조
              style={{ width: '11.5rem', height: '35px', paddingRight: '25px', fontSize: '0.85rem', borderRadius: '8px', border: '1px solid #CBD5E1', cursor: 'pointer' }}
            />
            <span
              onClick={() => startInputRef.current?.click()}
              style={{
                cursor: 'pointer',
                position: 'absolute',
                left: '9.5rem',
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 1
              }}
            >
              <FontAwesomeIcon icon={faCalendarAlt} style={{ color: '#64748B' }} />
            </span>
          </div>
          <div className="mb-0 fs-10" style={{ position: 'relative' }}>
            <input
              id="timepicker_end"
              className="form-control datetimepicker"
              type="text"
              placeholder="2024-12-31 00:00"
              title="작업일시"
              ref={endInputRef} // input 요소 참조
              style={{ width: '11.5rem', height: '35px', paddingRight: '25px', fontSize: '0.85rem', borderRadius: '8px', border: '1px solid #CBD5E1', cursor: 'pointer' }}
            />
            <span
              onClick={() => endInputRef.current?.click()} // click() 호출
              style={{
                cursor: 'pointer',
                position: 'absolute',
                left: '9.5rem',
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 1
              }}
            >
              <FontAwesomeIcon icon={faCalendarAlt} style={{ color: '#64748B' }} />
            </span>

          </div>
          <div className="mb-0 fs-10">
            <Form.Select
              size="sm"
              value={org}
              title="소속"
              onChange={e => setOrg(e.target.value)}
              style={{ height: '35px', fontSize: '0.85rem', padding: '0.3rem 2.25rem 0.3rem 0.75rem', borderRadius: '8px', border: '1px solid #CBD5E1' }}
            >
              <option value="" disabled hidden>
                소속
              </option>
              <option value="">전체</option>
              {companies.map(company => (
                <option key={company.companyId} value={company.companyId}>
                  {company.org}
                </option>
              ))}
            </Form.Select>
          </div>
          <div className="mb-0 fs-10">
            <Form.Select
              size="sm"
              value={role}
              title="역할"
              onChange={e => setRole(e.target.value)}
              style={{ height: '35px', fontSize: '0.85rem', padding: '0.3rem 2.25rem 0.3rem 0.75rem', borderRadius: '8px', border: '1px solid #CBD5E1' }}
            >
              <option value="" disabled hidden>
                역할
              </option>
              <option value="">전체</option>
              {roleOptions.map((roleName, index) => (
                <option key={index} value={roleName}>
                  {roleName}
                </option>
              ))}
            </Form.Select>
          </div>
          <div className="mb-0 fs-10">
            <Form.Control
              size="sm"
              type="text"
              value={uid}
              placeholder="아이디"
              title="아이디"
              onChange={e => setUid(e.target.value)}
              style={{ height: '35px', borderRadius: '8px', border: '1px solid #CBD5E1' }}
            />
          </div>
          <div className="mb-0 fs-10">
            <InputGroup style={{ width: '100%' }}>
              <Form.Control
                size="sm"
                type="text"
                title="작업내역"
                value={job}
                placeholder="작업내역"
                onChange={handleInputChange}
                style={{ height: '35px', width: '50%', borderTopLeftRadius: '8px', borderBottomLeftRadius: '8px', border: '1px solid #CBD5E1' }}
                maxLength="20"
                disabled={selectedOption !== '직접입력'} // "직접입력"이 아닌 경우 비활성화
              />
              <Form.Select
                size="sm"
                value={selectedOption} // 선택된 옵션 상태 사용
                onChange={handleSelectChange}
                style={{ height: '35px', fontSize: '0.85rem', padding: '0.3rem 1.8rem 0.3rem 0.5rem', width: '50%', borderTopRightRadius: '8px', borderBottomRightRadius: '8px', border: '1px solid #CBD5E1', borderLeft: 'none' }}
              >
                {options.map((option, index) => (
                  <option key={index} value={option}>
                    {option}
                  </option>
                ))}
              </Form.Select>
            </InputGroup>
          </div>
        </FilterInputs>

        {/* 구분선 */}

        <FilterButtons>
          <Button
            variant="secondary"
            onClick={handleDefault}
            title="초기화"
            style={{ height: '35px', minWidth: '70px', whiteSpace: 'nowrap', borderRadius: '8px', fontWeight: 700, backgroundColor: '#E2E8F0', color: '#475569', border: 'none', paddingTop: 0, paddingBottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            초기화
          </Button>
          <Button variant="primary" style={{ height: '35px', minWidth: '70px', whiteSpace: 'nowrap', borderRadius: '8px', fontWeight: 700, boxShadow: '0 2px 4px rgba(59, 130, 246, 0.2)', paddingTop: 0, paddingBottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={handleSearch}>
            검색
          </Button>
        </FilterButtons>
          </FilterContainer>
        </Card.Body>
      </StyledCard>
      <StyledCard>
        <AdvanceTable
          headerClassName="bg-light text-nowrap align-middle text-800"
          rowClassName="align-middle white-space-nowrap border-bottom border-200 cursor-pointer hover-bg-light transition-base"
          tableProps={{ hover: true, className: 'fs-10 mb-0 border-0' }}
          onRowClick={handleRowClick}
        />
        <Card.Footer className="bg-light py-3 border-top">
          <div className="fs--1 fw-semi-bold text-500 mb-2">
            총 <span className="text-primary fw-bold">{totalElements.toLocaleString()}</span>건
          </div>
          <div className="d-flex justify-content-center">
            <AdvanceTablePagination
              currentPage={currentPage}
              totalItems={totalElements}
              itemsPerPage={perPage}
              onChange={newPage => handlePageChange(newPage)}
            />
          </div>
        </Card.Footer>
      </StyledCard>
      <CheckOutModal
        show={resultShow}
        onHide={() => setResultShow(false)}
        onConfirm={() => setResultShow(false)}
        title="경고"
        body="시작 날짜는 종료 날짜 이전이어야 합니다."
        confirmText="확인"
        variant="warning"
        SubmitButton={true}
      />

      {/* 상세 정보 모달 */}
      <Modal
        show={detailModalShow}
        onHide={handleCloseDetailModal}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>로그 상세 정보</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRowData ? (
            <div>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>순번:</strong> {selectedRowData.original?.num || 'N/A'}
                </Col>
                <Col md={6}>
                  <strong>작업일시:</strong> {selectedRowData.original?.jobdate || 'N/A'}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>소속:</strong> {selectedRowData.original?.org || 'N/A'}
                </Col>
                <Col md={6}>
                  <strong>역할:</strong> {selectedRowData.original?.Drole || 'N/A'}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>아이디:</strong> {selectedRowData.original?.uid || 'N/A'}
                </Col>
                <Col md={6}>
                  <strong>작업내역:</strong> {selectedRowData.original?.job || 'N/A'}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>결과:</strong> {selectedRowData.original?.result || 'N/A'}
                </Col>
              </Row>
              <hr />
              <div>
                <h6 className="mb-3">요청 상세 데이터</h6>
                <JsonDisplay data={selectedRowData.original?.requestDetails} />
              </div>
            </div>
          ) : (
            <div>데이터를 불러오는 중...</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDetailModal}>
            닫기
          </Button>
        </Modal.Footer>
      </Modal>
    </AdvanceTableProvider>
  );
}

const logManage = () => (
  <PageContainer>
    <HeaderSection>
      <TitleWrapper>
        <IconWrapper>
          <FontAwesomeIcon icon={faClipboardList} />
        </IconWrapper>
        <div>
          <PageTitle>관리활동로그</PageTitle>
          <PageSubtitle>관리자의 시스템 및 데이터 접근 이력을 실시간으로 모니터링합니다.</PageSubtitle>
        </div>
      </TitleWrapper>
    </HeaderSection>
    <AdvanceTableExample />
  </PageContainer>
);

export default logManage;

const FilterContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 0.25rem;
  gap: 8px;

  /* @media (max-width: 1919px) {
    flex-direction: column;
    margin-bottom: 16px;
  } */
`;

const FilterInputs = styled.div`
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  gap: 8px;
`;

const FilterButtons = styled.div`
  display: flex;
  justify-content: end;
  align-items: center;
  gap: 0.2rem;

  @media (max-width: 1919px) {
    gap: 4px;
  }
`;

const FilterButton = styled(Button)`
  white-space: nowrap;

  @media (max-width: 1919px) {
    line-height: 0px; /* 버튼 높이와 동일하게 설정 */
    height:30px;
    padding: 4px 8px;
  }
`;
